import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { META_BLUE, NAVY, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST, WHATSAPP_GREEN } from 'styles/tokens';

export interface BreadcrumbStep {
  /** Short label shown in the trail (e.g. "Campaign", "AdSet"). */
  eyebrow: string;
  /** Truncated name for the level. */
  label: string;
  /** Click → pop the cascade back to this level. */
  onClick: () => void;
}

interface CascadingSheetProps {
  eyebrow?: string;
  title: string;
  accent?: 'meta' | 'whatsapp' | 'navy';
  /** Levels above this one. Rendered as clickable chips above the title. */
  trail?: BreadcrumbStep[];
  /** Show a back arrow next to the title (returns to the previous level). */
  onBack?: () => void;
  /** Show a close (×) on the right of the header (drawer root only). */
  onClose?: () => void;
  children?: React.ReactNode;
}

const accentColor: Record<NonNullable<CascadingSheetProps['accent']>, string> = {
  meta: META_BLUE,
  whatsapp: WHATSAPP_GREEN,
  navy: NAVY,
};

const CascadingSheet: React.FC<CascadingSheetProps> = ({
  eyebrow,
  title,
  accent = 'navy',
  trail,
  onBack,
  onClose,
  children,
}) => (
  <Box
    sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', bgcolor: SURFACE_CONTAINER_LOWEST }}
  >
    <Box sx={{ px: 3, pt: 2.25, pb: 1.75, bgcolor: SURFACE_CONTAINER_LOW }}>
      {trail && trail.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {trail.map((step, i) => (
            <Box key={`${step.eyebrow}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                component="button"
                onClick={step.onClick}
                sx={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  fontFamily: 'Jost, sans-serif',
                  color: alpha(NAVY, 0.65),
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  maxWidth: 180,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  transition: 'background-color 140ms ease, color 140ms ease',
                  '&:hover': { bgcolor: alpha(NAVY, 0.06), color: NAVY },
                }}
              >
                <Box component="span" sx={{ color: alpha(NAVY, 0.45), fontWeight: 500, mr: 0.5 }}>
                  {step.eyebrow}:
                </Box>
                {step.label}
              </Box>
              <ChevronRightIcon sx={{ color: alpha(NAVY, 0.3), fontSize: '0.95rem' }} />
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {onBack && (
          <IconButton onClick={onBack} size="small" sx={{ color: NAVY, mt: 0.25 }} aria-label="Volver">
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {eyebrow && (
            <Typography
              sx={{
                color: alpha(NAVY, 0.55),
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontSize: '0.68rem',
                fontWeight: 600,
                fontFamily: 'Jost, sans-serif',
              }}
            >
              <Box component="span" sx={{ color: accentColor[accent], mr: 0.5 }}>
                ●
              </Box>
              {eyebrow}
            </Typography>
          )}
          <Typography
            sx={{
              color: NAVY,
              fontFamily: 'Jost, sans-serif',
              fontWeight: 700,
              fontSize: '1.15rem',
              lineHeight: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>
        </Box>
        {onClose && (
          <IconButton onClick={onClose} size="small" sx={{ color: NAVY }} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    </Box>
    <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>{children}</Box>
  </Box>
);

export default CascadingSheet;
